import { useGetMarketAccountDetailsQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal, isRepoDeal } from '@src/types/OTC/otcGuards';
import { formatOtcDealDateTimeInTimezone } from '@src/utils/OTC/otcDealDisplayUtils';
import { formatHolidayCalendars, formatSettlementCycle } from '@src/utils/OTC/otcUtils';
import { EMPTY_OBJECT } from '@talos/kyoko';
import { useMemo } from 'react';
import { InspectorText } from '../InspectorText';
import type { DisplayOutputItem } from './types';

export const useTimingInformation = (instrumentData: OTCDealData): DisplayOutputItem => {
  const { data: marketAccountDetailsData } = useGetMarketAccountDetailsQuery(EMPTY_OBJECT);

  const timezone = useMemo(() => {
    const savedTimezone = instrumentData.DealInfo?.SettleCutoffTimezone;
    if (savedTimezone) {
      return savedTimezone;
    }
    const marketAccount = instrumentData.MarketAccount;
    if (!marketAccount || !marketAccountDetailsData) {
      return 'UTC';
    }
    const accountId = String(marketAccount);
    return marketAccountDetailsData.get(accountId)?.SettleCutoffTimezone ?? 'UTC';
  }, [instrumentData.DealInfo?.SettleCutoffTimezone, instrumentData.MarketAccount, marketAccountDetailsData]);

  const tradeTime = instrumentData.TradeTime ?? instrumentData.DealInfo?.TradeTime;
  const effectiveTime = instrumentData.EffectiveTime ?? instrumentData.DealInfo?.EffectiveTime;
  const expireTime = instrumentData.ExpireTime ?? instrumentData.DealInfo?.ExpiryTime;
  const settleTime = instrumentData.SettleTime ?? instrumentData.DealInfo?.SettleTime;

  const settleCycle = instrumentData.DealInfo?.SettleCycle;
  const holidayCalendars = instrumentData.DealInfo?.HolidayCalendars;

  const isLoanDeal = isFixedRateLoanDeal(instrumentData) || isFloatingRateLoanDeal(instrumentData);
  const isRepo = isRepoDeal(instrumentData);
  const tradeDateLabel = isLoanDeal ? 'Origination Date' : 'Trade Date';

  const expiryDateLabel = isRepo ? 'Repurchase Date' : isLoanDeal ? 'Maturity Date' : 'Expiration Date';

  const formatContractDateTime = (value: string | number | null | undefined) =>
    formatOtcDealDateTimeInTimezone(value, timezone);

  return {
    sectionTitle: 'Contract Dates',
    fields: [
      {
        fieldName: tradeDateLabel,
        fieldValue: <InspectorText>{formatContractDateTime(tradeTime)}</InspectorText>,
      },
      {
        fieldName: 'Effective Date',
        fieldValue: <InspectorText>{formatContractDateTime(effectiveTime)}</InspectorText>,
      },
      {
        fieldName: expiryDateLabel,
        fieldValue: <InspectorText>{formatContractDateTime(expireTime)}</InspectorText>,
      },
      {
        fieldName: 'Settlement Date',
        fieldValue: <InspectorText>{formatContractDateTime(settleTime)}</InspectorText>,
      },
      {
        fieldName: 'Settlement Cycle',
        fieldValue: <InspectorText>{formatSettlementCycle(settleCycle)}</InspectorText>,
      },
      {
        fieldName: 'Holiday Calendar',
        fieldValue: <InspectorText>{formatHolidayCalendars(holidayCalendars)}</InspectorText>,
      },
    ],
  };
};
